import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import AuthService from '../../../shared/services/AuthService';

const Container = styled.div`
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
`;

const Hint = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${(p) => (p.$secondary ? '#eceff1' : '#1976d2')};
  color: ${(p) => (p.$secondary ? '#333' : '#fff')};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 0.9rem; }
  th { color: #666; font-weight: 600; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.78rem;
  background: ${(p) => (p.$active ? '#E8F5E8' : '#FFEBEE')};
  color: ${(p) => (p.$active ? '#2E7D32' : '#C62828')};
`;

const ErrorMsg = styled.div`
  background: #FFEBEE; border: 1px solid #EF9A9A; color: #C62828;
  padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; font-size: 0.9rem;
`;

const WifiVpnClients = () => {
  const [clients, setClients] = useState([]);
  const [mac, setMac] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      setClients(await AuthService.getWifiVpnClients());
    } catch (e) {
      setError('Не удалось загрузить список: ' + (e.response?.data?.error || e.message));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!mac.trim()) {
      setError('Укажите MAC-адрес устройства');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await AuthService.createWifiVpnClient({ mac_address: mac.trim(), comment: comment.trim() });
      setMac('');
      setComment('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async (c) => {
    setError('');
    try {
      await AuthService.updateWifiVpnClient(c.id, { enabled: !c.enabled });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Удалить доступ «${c.comment || c.mac_address}»?`)) return;
    setError('');
    try {
      await AuthService.deleteWifiVpnClient(c.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <Container>
      <Title>Гостевой Wi-Fi: доступ через VPN</Title>
      <Hint>
        Устройства из этого списка выходят в интернет через туннель, остальные гости — напрямую.
        Изменения применяются на роутере в течение минуты.
        <br />
        MAC нужен <b>настоящий</b>, а не приватный: на телефоне в настройках сети H2O выберите
        «MAC-адрес телефона» вместо случайного, иначе адрес сменится и доступ отвалится.
      </Hint>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <Row>
          <Input
            placeholder="MAC (AA:BB:CC:DD:EE:FF)"
            value={mac}
            onChange={(e) => setMac(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <Input
            placeholder="Кто это (имя, устройство)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{ minWidth: 240, flex: 1 }}
          />
          <Button onClick={handleCreate} disabled={loading}>Добавить</Button>
        </Row>
      </Card>

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Кто</th>
              <th>MAC</th>
              <th>Адрес</th>
              <th>Доступ</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={5} style={{ color: '#888' }}>Список пуст</td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id}>
                <td>{c.comment || '—'}</td>
                <td>{c.mac_address}</td>
                <td>{c.ip_address}</td>
                <td><Badge $active={c.enabled}>{c.enabled ? 'включён' : 'выключен'}</Badge></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button $secondary onClick={() => toggleEnabled(c)}>
                    {c.enabled ? 'Выключить' : 'Включить'}
                  </Button>{' '}
                  <Button $secondary onClick={() => handleDelete(c)}>Удалить</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
};

export default WifiVpnClients;
